/**
 * "Write with AI" for a portable (non-CV) skill, as two steps:
 *
 *   1. describe - one idea field plus the icon and the model to draft with.
 *   2. review   - the generated draft in the normal edit form, then Create.
 *
 * The whole flow lives here so the web dialog (`NewSkillDialog.web`) and the
 * native sheet (`app/(app)/skills/build`) stay behaviourally identical and only
 * differ in how they render the two steps. Nothing in this module touches the
 * DOM or react-native, so it is safe to import from either platform file.
 *
 * The idea text is only ever a generation prompt: `mergeGeneratedPortableDraft`
 * throws it away and keeps the AI's name and description, which is the whole
 * point of the second step.
 */
import { useCallback, useMemo, useState } from 'react';
import {
  createSkill,
  generateSkillDraft,
  isEntitlementDenied,
  type SkillsApiError,
} from '../../lib/skills-api';
import {
  defaultBuilderDraft,
  draftToSkillInput,
  mergeGeneratedPortableDraft,
  parseTagsInput,
  toCatalogModel,
  type SkillBuilderDraft,
} from './skill-builder-shared';

export type PortableWriterStep = 'describe' | 'review';

export interface UsePortableSkillWriterOptions {
  /**
   * A 403 from generate or create. The caller opens the paywall and unwinds
   * its surface; the flow stops rather than showing an inline error the user
   * cannot act on.
   */
  onEntitlementDenied: (error: SkillsApiError) => void;
  /** Fires with the new skill id after a successful create. */
  onCreated: (skillId: string) => void;
  /** Fallback copy when the server sends no message of its own. */
  generateFailedMessage: string;
  createFailedMessage: string;
}

export interface PortableSkillWriter {
  step: PortableWriterStep;
  /** Step 1: what the skill should do, in the author's words. */
  idea: string;
  setIdea: (next: string) => void;
  /** Step 2: the reviewable draft (also holds icon + model during step 1). */
  draft: SkillBuilderDraft;
  patch: (partial: Partial<SkillBuilderDraft>) => void;
  /** AI tags, parsed for the read-only chips on the review step. */
  tags: string[];
  busy: boolean;
  /** Inline error copy; cleared by the next attempt. */
  error: string;
  clearError: () => void;
  canGenerate: boolean;
  canCreate: boolean;
  /** Step 1 → 2. */
  generate: () => Promise<void>;
  /** Re-runs generate with the same idea, keeping the icon and model edits. */
  regenerate: () => Promise<void>;
  /** Failure escape hatch: open the review form empty rather than dead-end. */
  writeManually: () => void;
  /**
   * Whether "Regenerate" applies: false on the manual escape hatch, where
   * there is no idea to re-run.
   */
  canRegenerate: boolean;
  create: () => Promise<void>;
}

function messageOf(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

export function usePortableSkillWriter(
  options: UsePortableSkillWriterOptions,
): PortableSkillWriter {
  const {
    onEntitlementDenied,
    onCreated,
    generateFailedMessage,
    createFailedMessage,
  } = options;

  const [step, setStep] = useState<PortableWriterStep>('describe');
  const [idea, setIdea] = useState('');
  const [draft, setDraft] = useState<SkillBuilderDraft>(() => ({
    ...defaultBuilderDraft(),
    kind: 'default',
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const patch = useCallback((partial: Partial<SkillBuilderDraft>) => {
    setDraft((prev) => ({ ...prev, ...partial }));
  }, []);

  const clearError = useCallback(() => setError(''), []);

  const runGenerate = useCallback(async () => {
    const prompt = idea.trim();
    if (!prompt || busy) return;
    setBusy(true);
    setError('');
    try {
      const result = await generateSkillDraft({
        prompt,
        kind: 'default',
        // The picker holds chat ids; the API takes skill catalog ids.
        model: toCatalogModel(draft.model),
      });
      setDraft((prev) => mergeGeneratedPortableDraft(prev, result));
      setStep('review');
    } catch (caught) {
      if (isEntitlementDenied(caught)) {
        onEntitlementDenied(caught);
        return;
      }
      setError(messageOf(caught, generateFailedMessage));
    } finally {
      setBusy(false);
    }
  }, [busy, draft.model, generateFailedMessage, idea, onEntitlementDenied]);

  const create = useCallback(async () => {
    if (busy) return;
    if (!draft.name.trim() || !draft.description.trim()) return;
    setBusy(true);
    setError('');
    try {
      const created = await createSkill(draftToSkillInput(draft));
      onCreated(created.skillId);
    } catch (caught) {
      if (isEntitlementDenied(caught)) {
        onEntitlementDenied(caught);
        return;
      }
      setError(messageOf(caught, createFailedMessage));
    } finally {
      setBusy(false);
    }
  }, [busy, createFailedMessage, draft, onCreated, onEntitlementDenied]);

  const writeManually = useCallback(() => {
    setError('');
    setStep('review');
  }, []);

  const tags = useMemo(() => parseTagsInput(draft.tags), [draft.tags]);

  return {
    step,
    idea,
    setIdea,
    draft,
    patch,
    tags,
    busy,
    error,
    clearError,
    canGenerate: idea.trim().length > 0 && !busy,
    canCreate:
      draft.name.trim().length > 0 && draft.description.trim().length > 0 && !busy,
    generate: runGenerate,
    regenerate: runGenerate,
    writeManually,
    canRegenerate: idea.trim().length > 0 && !busy,
    create,
  };
}
